/**
 * CouponsTab - Admin Coupon Management Component
 * Extracted from AdminPage.jsx for better code organization
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Ticket, Plus, Copy, Pencil, Trash2, CheckCircle2, XCircle } from 'lucide-react';
import { useAdminTranslation } from '../../utils/useAdminTranslation';

const API = process.env.REACT_APP_BACKEND_URL;

export const CouponsTab = () => {
  const { tx } = useAdminTranslation();
  
  // State
  const [coupons, setCoupons] = useState([]);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [couponForm, setCouponForm] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: '',
    min_purchase: '',
    max_uses: '',
    expires_at: '',
  });

  // Fetch coupons
  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API}/admin/coupons`);
      setCoupons(res.data || []);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    }
  };

  // Handlers
  const handleCreateCoupon = async () => {
    try {
      await axios.post(`${API}/admin/coupons`, {
        ...couponForm,
        discount_value: parseFloat(couponForm.discount_value) || 0,
        min_purchase: parseFloat(couponForm.min_purchase) || 0,
        max_uses: parseInt(couponForm.max_uses) || 0,
      });
      toast.success(tx('Cupón creado', 'Coupon created'));
      setCouponDialogOpen(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creating coupon');
    }
  };

  const handleUpdateCoupon = async () => {
    try {
      await axios.put(`${API}/admin/coupons/${editingCoupon.id}`, {
        ...couponForm,
        discount_value: parseFloat(couponForm.discount_value) || 0,
        min_purchase: parseFloat(couponForm.min_purchase) || 0,
        max_uses: parseInt(couponForm.max_uses) || 0,
      });
      toast.success(tx('Cupón actualizado', 'Coupon updated'));
      setCouponDialogOpen(false);
      resetForm();
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating coupon');
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm(tx('¿Eliminar este cupón?', 'Delete this coupon?'))) return;
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`);
      toast.success(tx('Cupón eliminado', 'Coupon deleted'));
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting coupon');
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      await axios.post(`${API}/admin/coupons/${couponId}/toggle`);
      toast.success(tx('Estado del cupón actualizado', 'Coupon status updated'));
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error toggling coupon');
    }
  };

  const resetForm = () => {
    setEditingCoupon(null);
    setCouponForm({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: '',
      min_purchase: '',
      max_uses: '',
      expires_at: '',
    });
  };

  const openNewCouponDialog = () => {
    resetForm();
    setCouponDialogOpen(true);
  };

  const openEditCoupon = (coupon) => {
    setEditingCoupon(coupon);
    setCouponForm({
      code: coupon.code,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value.toString(),
      min_purchase: coupon.min_purchase ? coupon.min_purchase.toString() : '',
      max_uses: coupon.max_uses ? coupon.max_uses.toString() : '',
      expires_at: coupon.expires_at ? coupon.expires_at.split('T')[0] : '',
    });
    setCouponDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(tx('Código copiado', 'Code copied'));
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              {tx('Gestión de Cupones', 'Coupon Management')}
            </CardTitle>
            <CardDescription>
              {tx('Crea cupones de descuento para tus clientes', 'Create discount coupons for your customers')}
            </CardDescription>
          </div>
          <Button onClick={openNewCouponDialog}>
            <Plus className="h-4 w-4 mr-2" />
            {tx('Nuevo Cupón', 'New Coupon')}
          </Button>
        </CardHeader>
        <CardContent>
          {coupons.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Ticket className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>{tx('No hay cupones creados', 'No coupons created')}</p>
              <p className="text-sm">{tx('Crea tu primer cupón para atraer nuevos clientes', 'Create your first coupon to attract new customers')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{tx('Código', 'Code')}</TableHead>
                  <TableHead>{tx('Descuento', 'Discount')}</TableHead>
                  <TableHead>{tx('Mín. Compra', 'Min. Purchase')}</TableHead>
                  <TableHead>{tx('Usos', 'Uses')}</TableHead>
                  <TableHead>{tx('Expira', 'Expires')}</TableHead>
                  <TableHead>{tx('Estado', 'Status')}</TableHead>
                  <TableHead className="text-right">{tx('Acciones', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {coupons.map((coupon) => (
                  <TableRow key={coupon.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="bg-slate-100 px-2 py-1 rounded font-mono font-bold text-primary">
                          {coupon.code}
                        </code>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(coupon.code)}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      {coupon.description && (
                        <p className="text-xs text-muted-foreground mt-1">{coupon.description}</p>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        {coupon.discount_type === 'percentage' ? `${coupon.discount_value}%` : `${coupon.discount_value}€`}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {coupon.min_purchase > 0 ? `${coupon.min_purchase}€` : '-'}
                    </TableCell>
                    <TableCell>
                      <span className={coupon.max_uses > 0 && coupon.current_uses >= coupon.max_uses ? 'text-red-600' : ''}>
                        {coupon.current_uses} / {coupon.max_uses > 0 ? coupon.max_uses : '∞'}
                      </span>
                    </TableCell>
                    <TableCell>
                      {coupon.expires_at ? (
                        <span className={new Date(coupon.expires_at) < new Date() ? 'text-red-600' : ''}>
                          {new Date(coupon.expires_at).toLocaleDateString()}
                        </span>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={coupon.is_active ? 'default' : 'secondary'}
                        className={coupon.is_active ? 'bg-green-500' : 'bg-gray-400'}
                      >
                        {coupon.is_active ? tx('Activo', 'Active') : tx('Inactivo', 'Inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleToggleCoupon(coupon.id)}>
                          {coupon.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEditCoupon(coupon)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteCoupon(coupon.id)} className="text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Coupon Dialog */}
      <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ticket className="h-5 w-5" />
              {editingCoupon ? tx('Editar Cupón', 'Edit Coupon') : tx('Nuevo Cupón', 'New Coupon')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Code */}
            <div className="space-y-2">
              <Label>{tx('Código *', 'Code *')}</Label>
              <Input
                value={couponForm.code}
                onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                placeholder="DESCUENTO20"
                disabled={!!editingCoupon}
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{tx('Descripción', 'Description')}</Label>
              <Input
                value={couponForm.description}
                onChange={(e) => setCouponForm({ ...couponForm, description: e.target.value })}
                placeholder={tx('Cupón de bienvenida', 'Welcome coupon')}
              />
            </div>

            {/* Discount Type & Value */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{tx('Tipo', 'Type')}</Label>
                <Select
                  value={couponForm.discount_type}
                  onValueChange={(v) => setCouponForm({ ...couponForm, discount_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">{tx('Porcentaje', 'Percentage')}</SelectItem>
                    <SelectItem value="fixed">{tx('Fijo (€)', 'Fixed (€)')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{tx('Valor *', 'Value *')}</Label>
                <Input
                  type="number"
                  value={couponForm.discount_value}
                  onChange={(e) => setCouponForm({ ...couponForm, discount_value: e.target.value })}
                  placeholder={couponForm.discount_type === 'percentage' ? '20' : '10'}
                />
              </div>
            </div>

            {/* Min Purchase & Max Uses */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{tx('Mín. Compra (€)', 'Min. Purchase (€)')}</Label>
                <Input
                  type="number"
                  value={couponForm.min_purchase}
                  onChange={(e) => setCouponForm({ ...couponForm, min_purchase: e.target.value })}
                  placeholder="0"
                />
              </div>
              <div className="space-y-2">
                <Label>{tx('Máx. Usos', 'Max. Uses')}</Label>
                <Input
                  type="number"
                  value={couponForm.max_uses}
                  onChange={(e) => setCouponForm({ ...couponForm, max_uses: e.target.value })}
                  placeholder={tx('∞', '∞')}
                />
              </div>
            </div>

            {/* Expiration */}
            <div className="space-y-2">
              <Label>{tx('Fecha de Expiración', 'Expiration Date')}</Label>
              <Input
                type="date"
                value={couponForm.expires_at}
                onChange={(e) => setCouponForm({ ...couponForm, expires_at: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCouponDialogOpen(false)}>
              {tx('Cancelar', 'Cancel')}
            </Button>
            <Button
              onClick={editingCoupon ? handleUpdateCoupon : handleCreateCoupon}
              disabled={!couponForm.code || !couponForm.discount_value}
            >
              {editingCoupon ? tx('Guardar', 'Save') : tx('Crear', 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CouponsTab;
